import React, { useState, useEffect } from "react";
import { Modal, Form, Input, InputNumber, Select, message, Button, DatePicker } from "antd";
import ProductsSoldServ from "../service/ProductsSoldServ";
import ProductServ from "../service/ProductServ";
import moment from "moment";

const { Option } = Select;

const AddProductSold = ({ open, onClose, productSoldDetails, isEditing, refreshProductsSold }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [fetchingProducts, setFetchingProducts] = useState(false);

    useEffect(() => {
        if (open) {
            fetchProducts();
            if (isEditing && productSoldDetails) {
                form.setFieldsValue({
                    ...productSoldDetails,
                    productId: productSoldDetails.productId?._id || productSoldDetails.productId,
                    soldAt: productSoldDetails.soldAt ? moment(productSoldDetails.soldAt) : moment(),
                });
            } else {
                form.setFieldsValue({
                    quantity: 1,
                    soldAt: moment(),
                });
            }
        }
    }, [open, isEditing, productSoldDetails]);

    const fetchProducts = async () => {
        try {
            setFetchingProducts(true);
            const response = await ProductServ.getSellerProducts({ limit: 1000 });
            const productsData = response?.data || response?.products || (Array.isArray(response) ? response : []);
            setProducts(productsData);
        } catch (error) {
            console.error("Error fetching products:", error);
            message.error("Failed to load products");
        } finally {
            setFetchingProducts(false);
        }
    };

    const handleProductChange = (productId) => {
        const product = products.find(p => p._id === productId);
        if (product) {
            const quantity = form.getFieldValue('quantity') || 1;
            form.setFieldsValue({ totalAmount: product.price * quantity });
        }
    };

    const handleQuantityChange = (quantity) => {
        const productId = form.getFieldValue('productId');
        const product = products.find(p => p._id === productId);
        if (product) {
            form.setFieldsValue({ totalAmount: product.price * quantity });
        }
    };

    const onFinish = async (values) => {
        try {
            setLoading(true);
            const payload = {
                ...values,
                soldAt: values.soldAt.toDate(),
            };

            if (isEditing) {
                await ProductsSoldServ.updateRecord(productSoldDetails._id, payload);
                message.success("Sales record updated successfully");
            } else {
                await ProductsSoldServ.addRecord(payload);
                message.success("Sales record added successfully");
            }

            form.resetFields();
            onClose();
            refreshProductsSold(true);
        } catch (error) {
            console.error("Error saving sales record:", error);
            message.error(error.message || "Failed to save record");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={isEditing ? "Edit Sales Record" : "Add Sales Record"}
            open={open}
            onCancel={onClose}
            footer={null}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
            >
                <Form.Item
                    name="productId"
                    label="Select Product"
                    rules={[{ required: true, message: "Please select a product" }]}
                >
                    <Select
                        showSearch
                        placeholder="Search and select product"
                        optionFilterProp="children"
                        loading={fetchingProducts}
                        onChange={handleProductChange}
                    >
                        {products.map((product) => (
                            <Option key={product._id} value={product._id}>
                                {product.name} (₹{product.price})
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="customerName"
                    label="Customer Name"
                    rules={[{ required: true, message: "Please enter customer name" }]}
                >
                    <Input placeholder="E.g. John Doe" />
                </Form.Item>

                <Form.Item
                    name="quantity"
                    label="Quantity"
                    rules={[{ required: true, message: "Please enter quantity" }]}
                >
                    <InputNumber
                        className="pcu-w-100"
                        min={1}
                        onChange={handleQuantityChange}
                    />
                </Form.Item>

                <Form.Item
                    name="totalAmount"
                    label="Total Amount (₹)"
                    rules={[{ required: true, message: "Please enter total amount" }]}
                >
                    <InputNumber className="pcu-w-100" min={0} />
                </Form.Item>

                <Form.Item
                    name="soldAt"
                    label="Date Sold"
                    rules={[{ required: true, message: "Please select date" }]}
                >
                    <DatePicker className="pcu-w-100" showTime />
                </Form.Item>

                <Form.Item
                    name="notes"
                    label="Notes"
                >
                    <Input.TextArea rows={2} placeholder="Optional notes about the sale" />
                </Form.Item>

                <Form.Item className="pcu-margin-bottom-0 pcu-text-right">
                    <Button className="pcu-margin-right-8" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {isEditing ? "Update Record" : "Save Record"}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AddProductSold;
